import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { customMeteringRules, subscriptionTypes } from 'rp-auth';
import { setActiveSeasons } from '../../utils/filter/timePeriods';
import fixMobileSafariWhiteScreenBug from '../../utils/fixMobileSafariWhiteScreenBug';
import analyticsPage from '../../analyticsPage';
import { getPageName } from '../../utils/getPageName';
import { restrictedPagesByCustomMetering } from '../nav/helpers/tabNames';
import { setSessionStorageItem } from '../../utils/sessionStorage';

const { ULTIMATE } = subscriptionTypes;
const Form = 'Form';
const COURSE_PROFILE = 'course_profile';

class PageProvider extends PureComponent {
    constructor(props) {
        super(props);

        const data = {
            ...window.PRELOADED_STATE,
            skybetDiffusionName: window.skybetDiffusionName,
            isSkybetMigrationEnabled: window.isSkybetMigrationEnabled,
        };

        this.state = {
            data,
            hasAccess: true,
            userRole: null,
            isFreeTrial: false,
            pageName: null,
        };

        const { pathname } = this.props.location;
        const pathParts = pathname.split('/').filter(Boolean);

        // NOTE: [JP-9795] This prevent trigger analytics when user go to horse profile and after that redirect automatically to /form
        if (pathParts.length > 3 && !pathParts.includes('/form')) {
            analyticsPage(data);
        }

        setActiveSeasons(data.seasons);
    }

    componentWillMount() {
        setSessionStorageItem('abTastyUserProps', JSON.stringify({
            isLogged: this.state.data.isLogged,
            userRole: this.state.data.originalSubscriptionLevel,
        }));
        // eslint-disable-next-line no-unused-vars
        const [type, id, name, tab, subTab] = this.props.location.pathname.split('/');
        const { userRole, isFreeTrial } = this.state.data;

        const pageName = getPageName(type);

        if (userRole !== ULTIMATE && pageName !== COURSE_PROFILE) {
            const currentTab = tab || Form;
            const tabName = currentTab.toLowerCase().replace('-', '_');
            const shouldUseCustomMetering = restrictedPagesByCustomMetering[pageName]
                .includes(tabName);

            // if page doesn't exist in metering, give access to it directly
            // P.S. That page might be behind MPP metering too
            // hasAccess is only for the custom metering rules
            const hasAccess = Boolean(customMeteringRules[userRole][pageName][tabName]);

            if (!hasAccess && tabName !== Form.toLowerCase() && shouldUseCustomMetering) {
                window.location.href = '/racecards/?authme';
            }

            this.setState({ hasAccess, userRole, pageName, isFreeTrial });
        }

        // user has Ultimate subscription
        if (userRole === ULTIMATE) {
            this.setState({ hasAccess: true, userRole: ULTIMATE, pageName });
        }
    }

    componentDidMount() {
        fixMobileSafariWhiteScreenBug();
        // add event listeners for focus, blur tab and unloading to remove the isFreeTrial
        // the event listeners will automatically detach when we go to another page because of SSR
        window.addEventListener('focus', this.onFocus);
        window.addEventListener('blur', this.onBlur);
        window.addEventListener('beforeunload', () => {
            window.removeEventListener('focus', this.onFocus);
            window.removeEventListener('blur', this.onBlur);
            sessionStorage.removeItem('isFreeTrial');
            sessionStorage.removeItem('freeTrialAllowedOnThisTab');
            sessionStorage.removeItem('freeTrialHeader');
        });
    }

    componentDidUpdate() {
        fixMobileSafariWhiteScreenBug();
    }

    onFocus = () => {
        // on retaining focus, set the isFreeTrial to freeTrialAllowedOnThisTab,
        // which has the info if there should be a free trial for the specific page and tab
        sessionStorage.setItem('isFreeTrial', sessionStorage.getItem('freeTrialAllowedOnThisTab'));
    }

    onBlur = () => {
        sessionStorage.removeItem('isFreeTrial');
    }

    render() {
        const {
            route: { Page },
            location,
            children,
        } = this.props;
        const [type, id, name, tab, subTab] = location.pathname.split('/');
        const { hash } = location;
        const { data, hasAccess, pageName, userRole, isFreeTrial: preloadedFreeTrial } = this.state;

        let isFreeTrialAllowed = preloadedFreeTrial;

        // If the page is horse_profile but not Entries tab, there shouldn't be a free trial - JP-9931
        // this includes the stallion book
        if (pageName === 'horse_profile' && tab !== 'Entries') {
            isFreeTrialAllowed = false;
        }

        localStorage.setItem('userRole', userRole);
        sessionStorage.setItem('freeTrialHeader', preloadedFreeTrial);
        sessionStorage.setItem('freeTrialAllowedOnThisTab', isFreeTrialAllowed);
        sessionStorage.setItem('isFreeTrial', isFreeTrialAllowed);

        return (
            <Page
                data={data}
                params={{ type, id, name, tab, hash, subTab }}
                hasAccess={hasAccess}
                userRole={userRole}
                pageName={pageName}
            >
                {children}
            </Page>
        );
    }
}

PageProvider.propTypes = {
    route: PropTypes.shape({
        Page: PropTypes.func.isRequired,
    }).isRequired,
    location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
    }).isRequired,
    children: PropTypes.node.isRequired,
};

export default PageProvider;



// WEBPACK FOOTER //
// ./src/js/shared/providers/PageProvider.jsx